import { useState, useCallback } from 'react';
import { generatePDFReport } from '../utils/pdfGenerator';

interface ReportData {
  transactions: Array<{
    date: string;
    description: string;
    category: string;
    amount: number;
  }> | null | undefined;
  dateRange: { start: string; end: string };
  totalIncome: number;
  totalExpenses: number;
  savingsRate: string;
  monthlyData: Array<{
    month: string;
    income: number;
    expenses: number;
    balance: number;
  }>;
}

interface UseN8NPdfReturn {
  isLoading: boolean;
  error: string | null;
  success: string | null;
  generatePdf: (data: ReportData) => Promise<void>;
  clearMessages: () => void;
}

/**
 * Hook para gerenciar a geração de PDF
 * Fornece estado de carregamento, erro e sucesso
 */
export const useN8NPdf = (): UseN8NPdfReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  const generatePdf = useCallback(
    async (data: ReportData) => {
      setIsLoading(true);
      clearMessages();

      try {
        // Validar dados
        if (!data || !data.transactions) {
          throw new Error('Dados inválidos para gerar PDF');
        }

        // Usar geração local (simples e funcional)
        generatePDFReport({
          transactions: data.transactions,
          dateRange: data.dateRange,
          totalIncome: data.totalIncome,
          totalExpenses: data.totalExpenses,
          savingsRate: data.savingsRate,
          monthlyData: data.monthlyData,
        });

        setSuccess('✓ PDF gerado com sucesso');
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao gerar PDF';
        setError(`✗ ${errorMessage}`);
        setSuccess(null);
      } finally {
        setIsLoading(false);
      }
    },
    [clearMessages]
  );

  return {
    isLoading,
    error,
    success,
    generatePdf,
    clearMessages,
  };
};
